import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as actions from '../../../../../../store';
import { shouldCancelDrag } from '../constants/constants';
import { isValidProjectionMatch } from '../utils/utils';
import { handleMatch } from '../helpers/tableActions';

// Drag handlers for matching findings (drag start and drag end)

export const useDragHandlers = (setDraggingActive) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const customOpacities = useSelector((state) => state.labeling?.customOpacities);
  const customMicrocalc = useSelector((state) => state.labeling?.customMicrocalc);
  const exam_id = useSelector((state) => state.results?.exam_id);

  // Drag start handler
  const handleDragStart = useCallback(
    (event) => {
      const target = event.activatorEvent?.target;

      if (shouldCancelDrag(target)) {
        return;
      }

      const boxIdentifier = event.active.id;
      setDraggingActive(true);
      dispatch(actions.setDraggingLesionId(boxIdentifier));
    },
    [setDraggingActive, dispatch]
  );

  // Drag over handler
  const handleDragOver = useCallback(
    (event) => {
      const { over } = event;
      if (over && over.data?.current?.type === 'opacity-drop-zone') {
        const lesionAllInfo = over.data.current.lesionAllInfo;
        const findingId = lesionAllInfo?.finding_id;
        dispatch(actions.setTargetLesionId(findingId));
      } else {
        dispatch(actions.setTargetLesionId(null));
      }
    },
    [dispatch]
  );

  // Drag end handler - handles matching findings
  const handleDragEnd = useCallback(
    async (event) => {
      const { active, over } = event;

      // Check if the drag was cancelled
      if (!active || !over) {
        setDraggingActive(false);
        dispatch(actions.setTargetLesionId(null));
        dispatch(actions.resetHoverState());

        // Reset draggingLesionId after a short delay to allow for possible match detection
        setTimeout(() => {
          dispatch(actions.setDraggingLesionId(null));
        }, 100);
        return;
      }

      try {
        // Check if we're dropping on a drop zone
        if (over && over.data?.current?.type === 'opacity-drop-zone') {
          const sourceLesionAllInfo = active?.data?.current?.lesionAllInfo;
          const targetLesionAllInfo = over?.data?.current?.lesionAllInfo;

          // Only proceed with matching if we have valid lesionAllInfo and they're different
          if (sourceLesionAllInfo && targetLesionAllInfo && active.id !== over.id) {
            dispatch(actions.setSavingPending(true));

            // Prevent matching if either is matched or clustered
            if (
              sourceLesionAllInfo?.matched ||
              targetLesionAllInfo?.matched ||
              sourceLesionAllInfo?.clustered ||
              targetLesionAllInfo?.clustered ||
              sourceLesionAllInfo?.isMatchedMainRow ||
              targetLesionAllInfo?.isMatchedMainRow
            ) {
              dispatch(
                actions.setSnackbar({
                  msg: 'diagnose.error_matching',
                  severity: 'warning',
                })
              );
              setDraggingActive(false);
              dispatch(actions.setSavingPending(false));
              dispatch(actions.setTargetLesionId(null));
              dispatch(actions.setDraggingLesionId(null));
              dispatch(actions.resetHoverState());
              return;
            }

            // Extract projection arrays from lesionAllInfo structure
            const getProjectionArray = (lesionAllInfo) => {
              if (!lesionAllInfo) return null;

              // If projection is already an array format [projection, class, db_index, ...]
              if (Array.isArray(lesionAllInfo.projection) && lesionAllInfo.projection.length >= 3) {
                return [
                  lesionAllInfo.projection[0],
                  lesionAllInfo.projection[1] || lesionAllInfo.finding_class || lesionAllInfo.class,
                  lesionAllInfo.projection[2] ?? lesionAllInfo.db_index ?? 0,
                ];
              }

              // If projection is a string, construct array
              if (typeof lesionAllInfo.projection === 'string') {
                return [
                  lesionAllInfo.projection,
                  lesionAllInfo.finding_class || lesionAllInfo.class,
                  lesionAllInfo.db_index ?? 0,
                ];
              }

              // Fallback: try to use unique_position
              if (Array.isArray(lesionAllInfo.unique_position) && lesionAllInfo.unique_position.length >= 3) {
                return [
                  lesionAllInfo.unique_position[0],
                  lesionAllInfo.unique_position[1] || lesionAllInfo.finding_class || lesionAllInfo.class,
                  lesionAllInfo.unique_position[2] ?? lesionAllInfo.db_index ?? 0,
                ];
              }

              return null;
            };

            const sourceProjection = getProjectionArray(sourceLesionAllInfo);
            const targetProjection = getProjectionArray(targetLesionAllInfo);

            if (
              !sourceProjection ||
              !targetProjection ||
              sourceProjection.length < 3 ||
              targetProjection.length < 3 ||
              !sourceProjection[0] ||
              !targetProjection[0]
            ) {
              console.error('Invalid projection extraction:', {
                sourceLesionAllInfo,
                targetLesionAllInfo,
                sourceProjection,
                targetProjection,
              });
              dispatch(
                actions.setSnackbar({
                  msg: 'diagnose.general_error',
                  severity: 'error',
                })
              );
              setDraggingActive(false);
              dispatch(actions.setSavingPending(false));
              dispatch(actions.setTargetLesionId(null));
              dispatch(actions.setDraggingLesionId(null));
              dispatch(actions.resetHoverState());
              return;
            }

            // Prevent invalid projection match
            const dragEndProjectionMatch = isValidProjectionMatch(sourceProjection[0], targetProjection[0]);

            if (!dragEndProjectionMatch) {
              dispatch(
                actions.setSnackbar({
                  msg: 'diagnose.error_same_proj',
                  severity: 'warning',
                })
              );
              setDraggingActive(false);
              dispatch(actions.setSavingPending(false));
              dispatch(actions.setTargetLesionId(null));
              dispatch(actions.setDraggingLesionId(null));
              dispatch(actions.resetHoverState());
              return;
            }

            // Use centralized match action
            await handleMatch(
              exam_id,
              sourceProjection,
              targetProjection,
              customMicrocalc,
              customOpacities,
              dispatch,
              actions,
              t
            );
          }
        }
      } catch (error) {
        dispatch(
          actions.setSnackbar({
            msg: 'diagnose.general_error',
            severity: 'error',
          })
        );
      } finally {
        setDraggingActive(false);
        dispatch(actions.setTargetLesionId(null));
        dispatch(actions.setDraggingLesionId(null));
        dispatch(actions.resetHoverState());
        dispatch(actions.setSavingPending(false));
      }
    },
    [setDraggingActive, exam_id, customMicrocalc, customOpacities]
  );

  return {
    handleDragStart,
    handleDragOver,
    handleDragEnd,
  };
};
